import { useState } from "react";

import { InfoTip } from "@/components/InfoTip";
import { TIPS } from "@/lib/glossary";
import type { Alert, Severity } from "@/types/api";

const PILL_CLASS: Record<Severity, string> = {
  HIGH: "pill-high",
  MEDIUM: "pill-medium",
  LOW: "pill-low",
};

const ROW_RING: Record<Severity, string> = {
  HIGH: "ring-severity-high/40",
  MEDIUM: "ring-severity-medium/40",
  LOW: "ring-severity-low/40",
};

interface Props {
  alerts: Alert[];
}

export function AlertsBanner({ alerts }: Props): JSX.Element {
  const [collapsed, setCollapsed] = useState(false);

  if (alerts.length === 0) {
    return (
      <section
        aria-label="Alerts"
        className="card flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <span className="pill-ok">All clear</span>
          <span className="text-sm text-ink-300">No compliance alerts</span>
        </div>
      </section>
    );
  }

  return (
    <section aria-label="Alerts" className="card">
      <header className="flex items-center justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-200 inline-flex items-center">
          Alerts ({alerts.length})
          <InfoTip {...TIPS.severity} />
        </h2>
        <button
          type="button"
          className="btn-primary py-1 px-2 text-xs"
          onClick={() => setCollapsed((c) => !c)}
          aria-expanded={!collapsed}
        >
          {collapsed ? "Show" : "Hide"}
        </button>
      </header>
      {!collapsed && (
        <ul className="mt-3 space-y-2">
          {alerts.map((a) => (
            <li
              key={`${a.code}:${a.title}`}
              className={`bg-ink-700/60 rounded-md px-3 py-2 ring-1 ${ROW_RING[a.severity]}`}
            >
              <div className="flex items-center gap-2">
                <span className={PILL_CLASS[a.severity]}>{a.severity}</span>
                <span className="font-medium text-ink-100">{a.title}</span>
              </div>
              <p className="mt-1 text-sm text-ink-300">{a.detail}</p>
              {a.related.length > 0 && (
                <p className="mt-1 text-xs text-ink-400">{a.related.join(", ")}</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
